import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Text from "components/atoms/text/Text";

const Container = styled.div`
    width: 100%;
`;

const ResearchItemWrap = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: auto;
    max-width: 180px;
    min-height: 20px;
`;

const ResearchItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${(props) => props.margin || "2px"};
    padding: 2px 4px;
    max-width: 180px;
    height: 20px;
    outline: none;
    background-color: var(--color-Research);
    border: transparent;
    border-radius: 2px;
    cursor: pointer;
    transition: all 120ms ease-in;

    &:hover {
        background-color: var(--color-ResearchActive);
    }
`;

const MoreResearch = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 0 0 0;
    padding: 2px 4px;
    max-width: 180px;
    height: 20px;
    outline: none;
    background-color: var(--color-ResearchActive);
    border: transparent;
    border-radius: 2px;
    cursor: pointer;
`;

const BlankBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    background-color: var(--color-BaseBlue);
    border-radius: 4px;
`;

const BlankText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MoreBox = styled.div`
    position: fixed;
    top: ${({ position }) => position.top}px;
    right: ${({ position }) => position.right}px;
    display: ${({ active }) => (active ? "block" : "none")};
    width: 240px;
    max-width: 240px;
    min-height: 80px;
    background-color: var(--color-White);
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    z-index: 1;
`;

const MoreBoxHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    width: 100%;
    height: 32px;
    border-bottom: solid 1px var(--color-Outline);
`;

const MoreContentWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 8px 8px 8px;
    max-width: 240px;
    max-height: 160px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const ResearchListTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})``;

const ResearchListText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
    ellipsis: true,
})``;

function ReferenceResearchList(props) {
    const moreBoxRef = useRef();
    const nav = useNavigate();
    const { space_uuid } = useParams();

    const [showResearchBox, setShowResearchBox] = useState(false);
    const [moreBoxPosition, setMoreBoxPosition] = useState({ top: 0, right: 0 });

    const handleMoreResearchClick = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setMoreBoxPosition({
            top: rect.top + rect.height + 8,
            right: window.innerWidth - rect.right,
        });
        setShowResearchBox((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moreBoxRef.current && !moreBoxRef.current.contains(event.target)) {
                setShowResearchBox(false);
            }
        };

        const handleScroll = () => {
            setShowResearchBox(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("scroll", handleScroll, true); // `true`로 설정해 버블링된 스크롤 이벤트도 감지

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("scroll", handleScroll, true);
        };
    }, []);

    return (
        <Container>
            {props.bibliography.researches?.length > 0 ? (
                <ResearchItemWrap>
                    <Tooltip message={"연구노트로 이동"}>
                        <ResearchItem
                            margin={"0 4px 0 0"}
                            style={
                                props.bibliography.researches.length > 1 ? { maxWidth: "140px" } : { maxWidth: "180px" }
                            }
                            onClick={(e) => {
                                e.stopPropagation();
                                nav(`/${space_uuid}/research/${props.bibliography.researches[0].research_no}`);
                            }}
                        >
                            <ResearchListText>{props.bibliography.researches[0].display_name}</ResearchListText>
                        </ResearchItem>
                    </Tooltip>
                    {props.bibliography.researches.length > 1 && (
                        <MoreResearch
                            onClick={(e) => {
                                handleMoreResearchClick(e);
                            }}
                            ref={moreBoxRef}
                        >
                            <ResearchListText>+{props.bibliography.researches.length - 1}</ResearchListText>
                            <MoreBox active={showResearchBox} position={moreBoxPosition}>
                                <MoreBoxHeader>
                                    <ResearchListTitle>연구노트</ResearchListTitle>
                                    <Icon
                                        name={"x"}
                                        size={"8"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowResearchBox(!showResearchBox);
                                        }}
                                    />
                                </MoreBoxHeader>
                                <MoreContentWrap>
                                    {props.bibliography.researches.map((research, index) => (
                                        <Tooltip message={"연구노트로 이동"} key={research + index}>
                                            <ResearchItem
                                                onClick={(e) => {
                                                    nav(`/${space_uuid}/research/${research.research_no}`);
                                                }}
                                            >
                                                <ResearchListText>{research.display_name}</ResearchListText>
                                            </ResearchItem>
                                        </Tooltip>
                                    ))}
                                </MoreContentWrap>
                            </MoreBox>
                        </MoreResearch>
                    )}
                </ResearchItemWrap>
            ) : (
                <BlankBox>
                    <BlankText>연결된 연구노트가 없습니다.</BlankText>
                </BlankBox>
            )}
        </Container>
    );
}

export default ReferenceResearchList;
